import { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Upload,
  Download,
  Trash2,
  FileText,
  Lock,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select } from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import {
  useQuarterlyReport,
  useQuarterlyReportInvoices,
  useUpdateQuarterlyReport,
  useFinalizeQuarterlyReport,
  useUploadQuarterlyReportDocument,
  useDeleteQuarterlyReportDocument,
} from '../hooks/useQuarterlyReports'
import { quarterlyReportApi } from '../services/quarterlyReports'
import { useBankAccounts } from '../hooks/useSettings'
import { QuarterlyReportStatus } from '../types/quarterlyReport'
import { Invoice } from '../types/invoice'
import { IncomingInvoice } from '../types/incomingInvoice'
import { useAuthStore } from '../stores/authStore'
import { toast } from '../hooks/useToast'
import { getApiError } from '../lib/api'

const statusKeys: Record<QuarterlyReportStatus, string> = {
  draft: 'quarterlyReports.status.draft',
  pending_payment: 'quarterlyReports.status.pendingPayment',
  finalized: 'quarterlyReports.status.finalized',
  archived: 'quarterlyReports.status.archived',
}

const statusVariants: Record<QuarterlyReportStatus, 'secondary' | 'warning' | 'success' | 'destructive' | 'default'> = {
  draft: 'secondary',
  pending_payment: 'warning',
  finalized: 'success',
  archived: 'default',
}

export default function QuarterlyReportDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'

  const reportId = Number(id)
  const { data: report, isLoading, error, refetch } = useQuarterlyReport(reportId)
  const { data: invoicesData } = useQuarterlyReportInvoices(reportId)
  const { data: bankAccounts } = useBankAccounts()

  const updateMutation = useUpdateQuarterlyReport()
  const finalizeMutation = useFinalizeQuarterlyReport()
  const uploadDocMutation = useUploadQuarterlyReportDocument()
  const deleteDocMutation = useDeleteQuarterlyReportDocument()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [activeTab, setActiveTab] = useState<'invoices' | 'documents' | 'notes'>('invoices')
  const [expandedOutgoing, setExpandedOutgoing] = useState<Set<number>>(new Set())
  const [expandedIncoming, setExpandedIncoming] = useState<Set<number>>(new Set())
  const [editNotes, setEditNotes] = useState<string | null>(null)
  const [editBankAccountId, setEditBankAccountId] = useState<number | null | undefined>(undefined)
  const [editChargeAmount, setEditChargeAmount] = useState<string | undefined>(undefined)
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false)

  const isFinalized = report?.status === 'finalized' || report?.status === 'archived'

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const toggleOutgoing = (id: number) => {
    setExpandedOutgoing(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleIncoming = (id: number) => {
    setExpandedIncoming(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const quarterLabel = report ? `Q${report.quarter} ${report.year}` : ''

  // --- Bank account / charge amount save ---
  const handleSaveBankAndCharge = async () => {
    if (!report) return
    const updates: Record<string, unknown> = {}
    if (editBankAccountId !== undefined) {
      updates.bankAccountId = editBankAccountId
    }
    if (editChargeAmount !== undefined) {
      updates.chargeAmount = editChargeAmount === '' ? null : Number(editChargeAmount)
    }
    if (Object.keys(updates).length === 0) return

    try {
      await updateMutation.mutateAsync({ id: report.id, data: updates as any })
      toast({ title: t('common.save'), description: t('quarterlyReports.detail.updated') })
      setEditBankAccountId(undefined)
      setEditChargeAmount(undefined)
    } catch (err) {
      const apiError = getApiError(err)
      toast({ variant: 'destructive', title: t('common.error'), description: apiError.message })
    }
  }

  // --- Notes save ---
  const handleSaveNotes = async () => {
    if (!report || editNotes === null) return
    try {
      await updateMutation.mutateAsync({ id: report.id, data: { notes: editNotes || null } })
      toast({ title: t('common.save'), description: t('quarterlyReports.detail.notesUpdated') })
      setEditNotes(null)
    } catch (err) {
      const apiError = getApiError(err)
      toast({ variant: 'destructive', title: t('common.error'), description: apiError.message })
    }
  }

  // --- Finalize ---
  const handleFinalize = async () => {
    if (!report) return
    try {
      await finalizeMutation.mutateAsync(report.id)
      toast({ title: t('quarterlyReports.detail.finalized'), description: t('quarterlyReports.detail.finalizedDesc') })
      setShowFinalizeConfirm(false)
    } catch (err) {
      const apiError = getApiError(err)
      toast({ variant: 'destructive', title: t('common.error'), description: apiError.message })
    }
  }

  // --- Document upload ---
  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !report) return

    const maxSize = 20 * 1024 * 1024
    if (file.size > maxSize) {
      toast({ variant: 'destructive', title: t('common.error'), description: t('quarterlyReports.detail.fileTooLarge') })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      await uploadDocMutation.mutateAsync({ reportId: report.id, file })
      toast({ title: t('quarterlyReports.detail.fileUploaded'), description: file.name })
    } catch (err) {
      const apiError = getApiError(err)
      toast({ variant: 'destructive', title: t('common.error'), description: apiError.message })
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // --- Document download ---
  const handleDownloadDocument = async (docId: number, filename: string) => {
    if (!report) return
    try {
      await quarterlyReportApi.downloadDocument(report.id, docId, filename)
    } catch (err) {
      const apiError = getApiError(err)
      toast({ variant: 'destructive', title: t('common.error'), description: apiError.message })
    }
  }

  // --- Document delete ---
  const handleDeleteDocument = async (docId: number, filename: string) => {
    if (!report) return
    if (!window.confirm(t('quarterlyReports.detail.confirmDeleteFile', { filename }))) return

    try {
      await deleteDocMutation.mutateAsync({ reportId: report.id, docId })
      toast({ title: t('quarterlyReports.detail.fileDeleted'), description: filename })
    } catch (err) {
      const apiError = getApiError(err)
      toast({ variant: 'destructive', title: t('common.error'), description: apiError.message })
    }
  }

  // --- Error state ---
  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('common.error')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  // --- Not found ---
  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold">{t('quarterlyReports.detail.notFound')}</h3>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/quarterly-reports')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('quarterlyReports.detail.backToList')}
        </Button>
      </div>
    )
  }

  const currentBankAccountId = editBankAccountId !== undefined ? editBankAccountId : report.bankAccountId
  const currentChargeAmount = editChargeAmount !== undefined ? editChargeAmount : (report.chargeAmount !== null ? String(report.chargeAmount) : '')
  const hasBankChargeChanges = editBankAccountId !== undefined || editChargeAmount !== undefined

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/dashboard/quarterly-reports')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{quarterLabel}</h1>
              <Badge variant={statusVariants[report.status] as any}>
                {t(statusKeys[report.status])}
              </Badge>
              {isFinalized && <Lock className="h-4 w-4 text-muted-foreground" />}
            </div>
            {report.finalizedAt && (
              <p className="text-sm text-muted-foreground">
                {t('quarterlyReports.detail.finalizedAt', { date: formatDate(report.finalizedAt) })}
              </p>
            )}
          </div>
        </div>

        {/* Finalize button (admin only, not yet finalized) */}
        {isAdmin && !isFinalized && (
          <div className="flex items-center gap-2">
            {!showFinalizeConfirm ? (
              <Button onClick={() => setShowFinalizeConfirm(true)}>
                <Lock className="mr-2 h-4 w-4" />
                {t('quarterlyReports.detail.finalize')}
              </Button>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-destructive p-2">
                <span className="text-sm text-destructive font-medium">
                  {t('quarterlyReports.detail.confirmFinalize')}
                </span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleFinalize}
                  disabled={finalizeMutation.isPending}
                >
                  {finalizeMutation.isPending ? t('common.loading') : t('common.confirm')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowFinalizeConfirm(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Quarter info card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('quarterlyReports.detail.quarterInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.year')}</span>
              <span className="text-sm font-medium">{report.year}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.quarter')}</span>
              <span className="text-sm font-medium">Q{report.quarter}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('common.status')}</span>
              <Badge variant={statusVariants[report.status] as any}>
                {t(statusKeys[report.status])}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.createdAt')}</span>
              <span className="text-sm font-medium">{formatDate(report.createdAt)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Bank account & charge amount card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('quarterlyReports.detail.paymentInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-sm">{t('quarterlyReports.detail.bankAccount')}</Label>
              {isAdmin && !isFinalized ? (
                <Select
                  value={currentBankAccountId !== null && currentBankAccountId !== undefined ? String(currentBankAccountId) : ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setEditBankAccountId(val ? Number(val) : null)
                  }}
                >
                  <option value="">{t('quarterlyReports.detail.selectBankAccount')}</option>
                  {bankAccounts?.map((ba) => (
                    <option key={ba.id} value={String(ba.id)}>
                      {ba.name} - {ba.bankName}
                    </option>
                  ))}
                </Select>
              ) : (
                <p className="text-sm font-medium">
                  {report.bankAccountId
                    ? bankAccounts?.find((ba) => ba.id === report.bankAccountId)?.name || `#${report.bankAccountId}`
                    : '-'}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-sm">{t('quarterlyReports.detail.chargeAmount')}</Label>
              {isAdmin && !isFinalized ? (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={currentChargeAmount}
                  onChange={(e) => setEditChargeAmount(e.target.value)}
                />
              ) : (
                <p className="text-sm font-medium">
                  {report.chargeAmount !== null ? formatCurrency(report.chargeAmount) : '-'}
                </p>
              )}
            </div>
            {isAdmin && !isFinalized && hasBankChargeChanges && (
              <Button
                size="sm"
                onClick={handleSaveBankAndCharge}
                disabled={updateMutation.isPending}
                className="w-full"
              >
                {updateMutation.isPending ? t('common.saving') : t('common.save')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Invoice summary card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('quarterlyReports.detail.invoiceSummary')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.outgoingCount')}</span>
              <span className="text-sm font-medium">{report.invoiceSummary?.outgoingCount ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.outgoingTotal')}</span>
              <span className="text-sm font-bold">{formatCurrency(report.invoiceSummary?.outgoingTotal ?? 0)}</span>
            </div>
            <div className="border-t my-1" />
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.incomingCount')}</span>
              <span className="text-sm font-medium">{report.invoiceSummary?.incomingCount ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('quarterlyReports.detail.incomingTotal')}</span>
              <span className="text-sm font-bold">{formatCurrency(report.invoiceSummary?.incomingTotal ?? 0)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['invoices', 'documents', 'notes'] as const).map((tab) => (
          <button
            key={tab}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {t(`quarterlyReports.detail.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'invoices' && (
        <div className="space-y-6">
          {/* Outgoing invoices */}
          {/* Outgoing invoices */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {t('quarterlyReports.detail.outgoingInvoices')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!invoicesData?.outgoing || invoicesData.outgoing.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-6">
                  <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('quarterlyReports.detail.noOutgoingInvoices')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="px-4 w-8"></TableHead>
                      <TableHead className="px-4">{t('quarterlyReports.detail.invoiceNumber')}</TableHead>
                      <TableHead className="px-4">{t('common.date')}</TableHead>
                      <TableHead className="px-4 hidden md:table-cell">{t('invoices.detail.recipientInfo')}</TableHead>
                      <TableHead className="px-4">{t('common.status')}</TableHead>
                      <TableHead className="px-4 text-right">{t('common.total')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesData.outgoing.map((inv: Invoice) => {
                      const isExpanded = expandedOutgoing.has(inv.id)
                      return (
                        <>
                          <TableRow
                            key={inv.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleOutgoing(inv.id)}
                          >
                            <TableCell className="px-4">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </TableCell>
                            <TableCell className="px-4 font-medium">{inv.invoiceNumber}</TableCell>
                            <TableCell className="px-4 text-muted-foreground">{formatDate(inv.date)}</TableCell>
                            <TableCell className="px-4 hidden md:table-cell">
                              <span className="truncate max-w-[200px] block">{inv.recipient?.name}</span>
                            </TableCell>
                            <TableCell className="px-4">
                              <Badge variant="secondary">{inv.status}</Badge>
                            </TableCell>
                            <TableCell className="px-4 text-right font-medium">
                              {formatCurrency(inv.totalAmount, inv.currency)}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow key={`${inv.id}-detail`}>
                              <TableCell colSpan={6} className="bg-muted/30 p-0">
                                <div className="p-4 space-y-4">
                                  {/* Sender & Recipient */}
                                  <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                      <p className="text-xs font-medium text-muted-foreground uppercase">{t('invoices.detail.senderInfo')}</p>
                                      <p className="text-sm font-semibold">{inv.sender?.name}</p>
                                      <p className="text-xs text-muted-foreground">NIF: {inv.sender?.nif}</p>
                                      {(inv.sender?.street || inv.sender?.city) && (
                                        <p className="text-xs text-muted-foreground">
                                          {[inv.sender.street, inv.sender.city, inv.sender.postalCode].filter(Boolean).join(', ')}
                                        </p>
                                      )}
                                      {inv.sender?.email && <p className="text-xs text-muted-foreground">{inv.sender.email}</p>}
                                    </div>
                                    <div className="space-y-1">
                                      <p className="text-xs font-medium text-muted-foreground uppercase">{t('invoices.detail.recipientInfo')}</p>
                                      <p className="text-sm font-semibold">{inv.recipient?.name}</p>
                                      <p className="text-xs text-muted-foreground">NIF: {inv.recipient?.nif}</p>
                                      {(inv.recipient?.street || inv.recipient?.city) && (
                                        <p className="text-xs text-muted-foreground">
                                          {[inv.recipient.street, inv.recipient.city, inv.recipient.postalCode].filter(Boolean).join(', ')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {/* Items */}
                                  {inv.items && inv.items.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground uppercase mb-2">{t('invoices.detail.itemsDetail')}</p>
                                      <div className="rounded-md border">
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              <TableHead className="px-3 text-xs">{t('invoices.form.description')}</TableHead>
                                              <TableHead className="px-3 text-xs text-right">{t('invoices.form.quantity')}</TableHead>
                                              <TableHead className="px-3 text-xs text-right">{t('invoices.form.priceWithoutVat')}</TableHead>
                                              <TableHead className="px-3 text-xs text-right">{t('invoices.form.vat')}</TableHead>
                                              <TableHead className="px-3 text-xs text-right">{t('invoices.form.subtotal')}</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {inv.items.map((item, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell className="px-3 text-sm">{item.description}</TableCell>
                                                <TableCell className="px-3 text-sm text-right">{item.quantity}</TableCell>
                                                <TableCell className="px-3 text-sm text-right">{formatCurrency(item.priceWithoutVat, inv.currency)}</TableCell>
                                                <TableCell className="px-3 text-sm text-right">{item.vatRate}%</TableCell>
                                                <TableCell className="px-3 text-sm text-right font-medium">{formatCurrency(item.subtotalWithVat, inv.currency)}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    </div>
                                  )}
                                  {/* Totals */}
                                  <div className="flex justify-end">
                                    <div className="space-y-1 text-sm w-64">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('invoices.detail.taxBase')}</span>
                                        <span>{formatCurrency(inv.baseAmount, inv.currency)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('invoices.detail.vatAmount')}</span>
                                        <span>{formatCurrency(inv.totalVat, inv.currency)}</span>
                                      </div>
                                      {inv.totalWithholding > 0 && (
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">{t('invoices.detail.withholding')}</span>
                                          <span>-{formatCurrency(inv.totalWithholding, inv.currency)}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between border-t pt-1 font-semibold">
                                        <span>{t('invoices.detail.grandTotal')}</span>
                                        <span>{formatCurrency(inv.totalAmount, inv.currency)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Payment info */}
                                  {inv.sender?.paymentDetails?.iban && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">{t('invoices.detail.paymentInfo')}</p>
                                      <p className="text-xs text-muted-foreground">
                                        IBAN: {inv.sender.paymentDetails.iban}
                                        {inv.sender.paymentDetails.swift && ` | SWIFT: ${inv.sender.paymentDetails.swift}`}
                                        {inv.sender.paymentDetails.bankName && ` | ${inv.sender.paymentDetails.bankName}`}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Incoming invoices */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {t('quarterlyReports.detail.incomingInvoices')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!invoicesData?.incoming || invoicesData.incoming.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-6">
                  <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('quarterlyReports.detail.noIncomingInvoices')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="px-4 w-8"></TableHead>
                      <TableHead className="px-4">{t('quarterlyReports.detail.invoiceNumber')}</TableHead>
                      <TableHead className="px-4">{t('common.date')}</TableHead>
                      <TableHead className="px-4 hidden md:table-cell">{t('incomingInvoices.table.supplier')}</TableHead>
                      <TableHead className="px-4">{t('common.status')}</TableHead>
                      <TableHead className="px-4 text-right">{t('common.total')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesData.incoming.map((inv: IncomingInvoice) => {
                      const isExpanded = expandedIncoming.has(inv.id)
                      return (
                        <>
                          <TableRow
                            key={inv.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleIncoming(inv.id)}
                          >
                            <TableCell className="px-4">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </TableCell>
                            <TableCell className="px-4 font-medium">{inv.invoiceNumber}</TableCell>
                            <TableCell className="px-4 text-muted-foreground">{formatDate(inv.date)}</TableCell>
                            <TableCell className="px-4 hidden md:table-cell">
                              <span className="truncate max-w-[200px] block">{inv.supplier?.name}</span>
                            </TableCell>
                            <TableCell className="px-4">
                              <Badge variant="secondary">{inv.status}</Badge>
                            </TableCell>
                            <TableCell className="px-4 text-right font-medium">
                              {formatCurrency(inv.totalAmount, inv.currency)}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow key={`${inv.id}-detail`}>
                              <TableCell colSpan={6} className="bg-muted/30 p-0">
                                <div className="p-4 space-y-3">
                                  {/* Supplier info */}
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-muted-foreground uppercase">{t('incomingInvoices.detail.supplierInfo')}</p>
                                    <p className="text-sm font-semibold">{inv.supplier?.name}</p>
                                    {inv.supplier?.taxId && <p className="text-xs text-muted-foreground">NIF: {inv.supplier.taxId}</p>}
                                  </div>
                                  {/* Details */}
                                  <div className="grid gap-3 sm:grid-cols-3">
                                    <div>
                                      <p className="text-xs text-muted-foreground">{t('incomingInvoices.form.invoiceNumber')}</p>
                                      <p className="text-sm font-medium">{inv.invoiceNumber}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">{t('incomingInvoices.form.date')}</p>
                                      <p className="text-sm font-medium">{formatDate(inv.date)}</p>
                                    </div>
                                    {inv.receivedDate && (
                                      <div>
                                        <p className="text-xs text-muted-foreground">{t('incomingInvoices.form.receivedDate')}</p>
                                        <p className="text-sm font-medium">{formatDate(inv.receivedDate)}</p>
                                      </div>
                                    )}
                                  </div>
                                  {/* Total */}
                                  <div className="flex justify-end">
                                    <div className="text-sm w-64">
                                      <div className="flex justify-between border-t pt-1 font-semibold">
                                        <span>{t('incomingInvoices.detail.totalAmount')}</span>
                                        <span>{formatCurrency(inv.totalAmount, inv.currency)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Notes */}
                                  {inv.notes && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">{t('incomingInvoices.detail.notes')}</p>
                                      <p className="text-sm whitespace-pre-wrap">{inv.notes}</p>
                                    </div>
                                  )}
                                  {/* Files */}
                                  {inv.files && inv.files.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1">{t('incomingInvoices.files.title')}</p>
                                      <div className="flex flex-wrap gap-2">
                                        {inv.files.map(f => (
                                          <span key={f.id} className="inline-flex items-center gap-1 text-xs bg-background border rounded px-2 py-1">
                                            <FileText className="h-3 w-3" />
                                            {f.originalFilename}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'documents' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('quarterlyReports.detail.documents')}
              </CardTitle>
              {!isFinalized && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv,.doc,.docx"
                    className="hidden"
                    onChange={handleUploadDocument}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadDocMutation.isPending}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploadDocMutation.isPending
                      ? t('common.loading')
                      : t('quarterlyReports.detail.uploadDocument')}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {report.documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center px-6">
                <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">{t('quarterlyReports.detail.noDocuments')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-4">{t('quarterlyReports.detail.filename')}</TableHead>
                    <TableHead className="px-4">{t('quarterlyReports.detail.fileSize')}</TableHead>
                    <TableHead className="px-4">{t('quarterlyReports.detail.uploadedAt')}</TableHead>
                    <TableHead className="px-4 w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="px-4">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div>
                            <span className="truncate max-w-[300px] block">{doc.originalFilename}</span>
                            {doc.description && (
                              <span className="text-xs text-muted-foreground">{doc.description}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 text-muted-foreground">
                        {formatFileSize(doc.fileSize)}
                      </TableCell>
                      <TableCell className="px-4 text-muted-foreground">
                        {formatDate(doc.uploadedAt)}
                      </TableCell>
                      <TableCell className="px-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadDocument(doc.id, doc.originalFilename)}
                            title={t('quarterlyReports.detail.download')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {!isFinalized && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeleteDocument(doc.id, doc.originalFilename)}
                              disabled={deleteDocMutation.isPending}
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'notes' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {t('quarterlyReports.detail.notes')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isAdmin && !isFinalized ? (
              <>
                <Textarea
                  rows={6}
                  placeholder={t('quarterlyReports.detail.notesPlaceholder')}
                  value={editNotes !== null ? editNotes : (report.notes ?? '')}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
                <Button
                  size="sm"
                  onClick={handleSaveNotes}
                  disabled={updateMutation.isPending || editNotes === null}
                >
                  {updateMutation.isPending ? t('common.saving') : t('common.save')}
                </Button>
              </>
            ) : (
              <p className="text-sm whitespace-pre-wrap">
                {report.notes || t('quarterlyReports.detail.noNotes')}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
